import React from 'react';
import type { LayerRenderer } from '../../../types';
import { LINE_STYLES, getLineDashArray } from '../../../layers/symbols';
import { ColorField, PropertySection, SliderField } from './SymbolProperties';

/** Line symbology: stroke style, colour, width, opacity, with a live preview. */
export default function LineSymbolEditor({
  renderer,
  onChange,
}: {
  renderer: LayerRenderer;
  onChange: (changes: Partial<LayerRenderer>) => void;
}) {
  const activeStyle = renderer.lineStyle ?? 'solid';

  return (
    <>
      <PropertySection title="Line style">
        <div className="space-y-1">
          {LINE_STYLES.map((style) => {
            const isSelected = style.id === activeStyle;
            return (
              <button
                key={style.id}
                type="button"
                aria-pressed={isSelected}
                title={style.name}
                onClick={() => onChange({ lineStyle: style.id })}
                className={`flex w-full items-center gap-3 rounded-lg border px-2.5 py-2 transition ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <span className="w-20 flex-shrink-0 text-left text-[11px] font-medium text-slate-700">
                  {style.name}
                </span>
                <svg viewBox="0 0 100 8" className="h-2 flex-1" preserveAspectRatio="none">
                  <line
                    x1="0"
                    y1="4"
                    x2="100"
                    y2="4"
                    stroke={renderer.color}
                    strokeWidth={3}
                    strokeDasharray={style.dashArray}
                  />
                </svg>
              </button>
            );
          })}
        </div>
      </PropertySection>

      <PropertySection title="Appearance">
        {renderer.type === 'single' && (
          <ColorField label="Colour" value={renderer.color} onChange={(color) => onChange({ color })} />
        )}
        <SliderField
          label="Width"
          value={renderer.outlineWidth}
          min={0.5}
          max={12}
          step={0.5}
          suffix="px"
          onChange={(outlineWidth) => onChange({ outlineWidth })}
        />
        <SliderField
          label="Opacity"
          value={Math.round(renderer.fillOpacity * 100)}
          min={0}
          max={100}
          suffix="%"
          onChange={(value) => onChange({ fillOpacity: value / 100 })}
        />
      </PropertySection>

      <PropertySection title="Preview">
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-4">
          <svg viewBox="0 0 200 20" className="h-5 w-full" preserveAspectRatio="none">
            <line
              x1="0"
              y1="10"
              x2="200"
              y2="10"
              stroke={renderer.color}
              strokeWidth={renderer.outlineWidth}
              strokeOpacity={renderer.fillOpacity}
              strokeDasharray={getLineDashArray(activeStyle)}
            />
          </svg>
        </div>
      </PropertySection>
    </>
  );
}
